import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Link, Modal, ProfileImage, ProfileInitials } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { format } from 'date-fns';
import { capitalize } from 'lodash';

import { getDuplicateRider } from '../../../api/services/Riders';
import { formatDate, formattedIsoDate } from '../../../utils/helpers/formatDate';

import { StyledBox } from './styles';

export const DuplicateRiderModal: FC<{ slug?: string }> = ({ slug }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { watch, formState } = useFormContext();
  const [duplicateRiders, setDuplicateRiders] = useState<Rider[]>([]);

  const [firstName, lastName, dateOfBirth, gender] = watch(['firstName', 'lastName', 'dateOfBirth', 'gender']);

  const requestDuplicateRiders = async (fields, signal) => {
    if (fields.dateOfBirth instanceof Date && !fields.dateOfBirth.getDate()) {
      return;
    }

    const response = await getDuplicateRider(
      {
        ...fields,
        dateOfBirth: format(fields.dateOfBirth, 'dd/MM/yyyy'),
      },
      signal
    );

    if (response?.type === 'success') {
      const filterCurrentRider = response.data.duplicateRiders.filter((r) => r.slug !== slug);

      if (filterCurrentRider.length) {
        setIsOpen(true);
        setDuplicateRiders(filterCurrentRider);
      }
    }
  };

  useEffect(() => {
    const abortController: AbortController = new AbortController();
    // setting a timeout allows the user to continue typing before we request. With react-hook-form, there is not a way to know when user is finished typing
    const timeout = setTimeout(() => {
      if (!formState.isDirty || [firstName, lastName, dateOfBirth, gender].some((value) => !value)) {
        return;
      }
      requestDuplicateRiders({ firstName, lastName, gender, dateOfBirth }, abortController.signal);
    }, 1500);

    return () => {
      timeout && clearTimeout(timeout);
      abortController.abort();
    };
  }, [firstName, lastName, gender, dateOfBirth, formState.isDirty]);

  return (
    <Modal open={isOpen} maxWidth="sm">
      <Modal.Title>Duplicate rider</Modal.Title>
      <Modal.Content>
        <StyledBox display="flex" flexDirection="column" gap="1rem">
          {
            'It looks like one or more similar riders already exist. Would you like to modify any of these existing riders?'
          }
          {duplicateRiders.map((rider) => (
            <Box key={rider.slug} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap="8px">
                {rider.profilePhotoUrl && rider.profilePhotoUrl.indexOf('default_photo') === -1 ? (
                  <ProfileImage imgSrc={rider.profilePhotoUrl} />
                ) : (
                  <ProfileInitials fullName={`${rider.firstName} ${rider.lastName}`} />
                )}

                <Link href={`/riders/${rider.slug}`} target="_blank" rel="noreferrer">
                  <>
                    {rider.firstName} {rider.lastName}
                  </>
                </Link>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="flex-end">
                <div>{capitalize(rider.gender)}</div>
                <div>{formatDate(new Date(formattedIsoDate(rider.dateOfBirth)), 'MM/dd/yyyy')}</div>
              </Box>
            </Box>
          ))}
        </StyledBox>
      </Modal.Content>
      <Modal.Actions>
        <Button color="primary" onClick={() => setIsOpen(false)}>
          Continue with New Rider
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
